import { useState, useContext } from 'react'
import ComparisonFrame from './ComparisonFrame'
import AnalysisItemNavigator from '../analysis/AnalysisItemNavigator'
import DeepAnalysisSlotsContext from '../../StateManagment/DeepAnalysisSlotsContext'
import slotsForSide from '../../helpers/deepAnalysis/slotsForSide'

// AI-CONTEXT: See docs/features/compare-two-periods.md
// Compare layout for a "parent" card (Effect / Activity / Research) whose header
// and item-navigator live above a multi-item visualisation. Rendering the whole
// body twice would duplicate the header (and the pin buttons); the navigator,
// hidden per-side, would also strand both sides on the first item. So this owns
// those shared pieces ONCE:
//   - the header renders once, above both panels;
//   - a single navigator drives which item BOTH periods show (shared visible);
//   - only the chart renders twice, via ComparisonFrame small multiples.
//
// The child viz reports its item list through the nav.setItems callback (so the
// navigator knows the count) and reads the shared nav.visible / nav.setVisible,
// with its own chrome suppressed (hideControls).
//
// On the deep-analysis page each side also gets its own detail panes
// (slotsForSide), so the two periods' facts don't land in one pane.
// AI-CONTEXT: See docs/features/deep-analysis-period.md

/**
 * @param {object} props
 * @param {React.ReactNode} props.header - The card header, rendered once above both panels
 * @param {{start: string|null, end: string|null}|null} props.rangeA - Primary period
 * @param {{start: string|null, end: string|null}|null} props.rangeB - Compare period
 * @param {(range: {start: string|null, end: string|null}|null, side: 'A'|'B', nav: {visible: number, setVisible: React.Dispatch<React.SetStateAction<number>>, setItems: (items: Array<unknown>) => void}) => React.ReactNode} props.renderViz - Renders one period's chart-only viz, wired to the shared nav
 * @returns {JSX.Element}
 */
const ParentCompareView = ({ header, rangeA, rangeB, renderViz }) => {
    const [visible, setVisible] = useState(0)
    const [items, setItems] = useState([])
    const slots = useContext(DeepAnalysisSlotsContext)

    const toolbar = items.length > 1
        ? <AnalysisItemNavigator items={items} visible={visible} setVisible={setVisible} />
        : null

    return (
        <>
            {header}
            <ComparisonFrame
                rangeA={rangeA}
                rangeB={rangeB}
                toolbar={toolbar}
                renderViz={(range, side) => (
                    <DeepAnalysisSlotsContext.Provider value={slotsForSide(slots, side)}>
                        {renderViz(range, side, { visible, setVisible, setItems })}
                    </DeepAnalysisSlotsContext.Provider>
                )}
            />
        </>
    )
}

export default ParentCompareView
